import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

interface ConnectionAlerts {
  whatsappDisconnected: boolean;
  openaiMissing: boolean;
  loading: boolean;
}

interface CheckConnectionResponse {
  success: boolean;
  connected: boolean;
  state?: string;
  instanceName?: string;
  error?: string;
}

export const useConnectionAlerts = (): ConnectionAlerts => {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState<ConnectionAlerts>({
    whatsappDisconnected: false,
    openaiMissing: false,
    loading: true
  });

  useEffect(() => {
    if (!user?.account_id) {
      setAlerts({ whatsappDisconnected: false, openaiMissing: false, loading: false });
      return;
    }

    const checkConnectionStatus = async () => {
      try {
        if (!supabase) {
          console.error('[useConnectionAlerts] Supabase client not initialized');
          setAlerts({ whatsappDisconnected: false, openaiMissing: false, loading: false });
          return;
        }

        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
        const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

        const checkConnectionUrl = `${supabaseUrl}/functions/v1/check-connection?account_id=${user.account_id}`;

        const [whatsappResponse, openaiResult] = await Promise.all([
          fetch(checkConnectionUrl, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${supabaseAnonKey}`,
              'Content-Type': 'application/json',
            },
          }),
          supabase
            .from('account_integrations')
            .select('token_openai')
            .eq('account_id', user.account_id)
            .maybeSingle()
        ]);

        let whatsappDisconnected = true;

        if (whatsappResponse.ok) {
          const whatsappData: CheckConnectionResponse = await whatsappResponse.json();
          console.log('[useConnectionAlerts] WhatsApp connection check:', whatsappData);
          whatsappDisconnected = !whatsappData.connected;
        } else {
          console.error('[useConnectionAlerts] Error checking WhatsApp connection:', whatsappResponse.status);
        }

        const openaiMissing =
          !openaiResult.data ||
          !openaiResult.data.token_openai ||
          openaiResult.data.token_openai.trim() === '';

        setAlerts({
          whatsappDisconnected,
          openaiMissing,
          loading: false
        });

      } catch (error) {
        console.error('[useConnectionAlerts] Error checking connection status:', error);
        setAlerts({
          whatsappDisconnected: false,
          openaiMissing: false,
          loading: false
        });
      }
    };

    checkConnectionStatus();
  }, [user?.account_id]);

  return alerts;
};
